/**
 * Customer Journey List Configuration
 * Shared config for QualifiedProspects and CoolLead tabs
 */
import PrivacyField from '@/components/PrivacyField'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { createDateFormatter } from '@/components/ui/data-table-customized'
import { CUSTOMER_JOURNEYS } from '@/modules/Customer/constants'
import type {
    CustomerJourney,
    CustomerJourneyNote,
    CustomerQualifiedProspect,
} from '@/modules/Customer/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import {
    getCommonCustomerFilters,
    JOURNEY_FILTER_DEFAULTS,
} from './customer-list-shared-config'
import { parseImportantJourneyNote } from './notes'

dayjs.extend(isoWeek)

// ============================================================================
// Types
// ============================================================================

interface WeekRange {
    weekNumber: number
    start: Dayjs
    end: Dayjs
    label: string
}

// ============================================================================
// Main Customer Columns (parent rows)
// ============================================================================

export const getJourneyCustomerColumns = (
    t: TFunction
): DataTableColumn<CustomerQualifiedProspect>[] => [
    {
        key: 'customer_name',
        title: t('customer_name'),
        width: 300,
        pinned: 'left',
        sortable: true,
        cellClassName: 'truncate',
        render: (_, row) => (
            <Link
                to={generatePathDatacentral(paths.customerDetail, {
                    id: row.customer_id,
                })}
                className="text-primary hover:underline uppercase"
            >
                {row.customer_id} - {row.company_name || row.customer_name}
            </Link>
        ),
    },
    {
        key: 'customer_journey',
        title: t('customer_sales_followers'),
        width: 200,
        cellClassName: 'truncate',
        render: (_, row) => {
            // Extract unique sale followers from all journeys
            const followersMap = new Map<number, string>()
            row.customer_journey?.forEach((j) => {
                j.customer_journey_explaination?.sale_tracking_id?.forEach(
                    (s: any) => {
                        if (s?.employee_id && s?.employee_name) {
                            followersMap.set(s.employee_id, s.employee_name)
                        }
                    }
                )
            })
            const names = [...followersMap.values()].join(', ')
            if (!names)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-sales-assigned')}
                    </span>
                )
            return (
                <span className="truncate block" title={names}>
                    {names}
                </span>
            )
        },
    },
    {
        cellClassName: 'truncate',
        key: 'customer_service_interestings',
        title: t('customer_service_interestings'),
        width: 250,
        render: (_, row) => {
            // Collect unique service interestings from all journeys
            const servicesSet = new Set<string>()
            row.customer_journey?.forEach((j) => {
                j.customer_journey_explaination?.service_interestings?.forEach(
                    (s: string) => s && servicesSet.add(s)
                )
            })
            const services = [...servicesSet].join(', ')
            if (!services)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-cs-assigned')}
                    </span>
                )
            return (
                <span className="truncate block" title={services}>
                    {services}
                </span>
            )
        },
    },
    {
        key: 'customer_industry',
        title: t('customer_industry'),
        width: 150,
        render: (value) => <span className="capitalize">{value || '-'}</span>,
    },
    {
        key: 'customer_email',
        title: t('customer_email'),
        width: 250,
        render: (value) => (
            <PrivacyField value={value} type="email" className="w-full" />
        ),
    },
    {
        key: 'customer_created_at',
        title: t('created_at'),
        width: 120,
        sortable: true,
        cellClassName: 'text-right text-gray-400 italic',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
    },
    {
        key: 'customer_updated_at',
        title: t('updated_at'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right text-gray-400 italic',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
    },
]

// ============================================================================
// Expanded Journey Columns (child rows)
// ============================================================================

export const getJourneyExpandedColumns = (
    t: TFunction
): DataTableColumn<CustomerJourney>[] => [
    {
        key: 'customer_journey_step',
        title: t('journey_step'),
        width: 150,
        render: (value) => {
            const step = CUSTOMER_JOURNEYS(t)?.find((j) => j.value === value)
            return (
                <span className="text-xs">{step?.label || value || '-'}</span>
            )
        },
    },
    {
        key: 'customer_journey_explaination',
        title: t('customer_sales_followers'),
        width: 250,
        cellClassName: 'truncate',
        render: (value) => {
            const names = value?.sale_tracking_id
                ?.map((item: any) => item?.employee_name)
                ?.join(', ')
            if (!names)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-sales-assigned')}
                    </span>
                )
            return (
                <span className="truncate block" title={names}>
                    {names}
                </span>
            )
        },
    },
    {
        key: 'service_interestings',
        title: t('customer_service_interestings'),
        width: 300,
        render: (_, row) => {
            const services =
                row.customer_journey_explaination?.service_interestings?.join(
                    ', '
                )
            if (!services)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-services-assigned')}
                    </span>
                )
            return (
                <span className="truncate block" title={services}>
                    {services}
                </span>
            )
        },
    },
    {
        key: 'budget',
        title: t('budget_per_month'),
        width: 150,
        cellClassName: 'text-right',
        render: (_, row) => {
            const budget =
                row.customer_journey_explaination?.budget_explaination
            if (!budget || budget === 0)
                return (
                    <span className="text-gray-400 italic">
                        {t('no-budget')}
                    </span>
                )
            return new Intl.NumberFormat('vi-VN').format(budget)
        },
    },
    {
        key: 'customer_journey_created_at',
        title: t('created_at'),
        cellClassName: 'text-right text-gray-400 italic',
        width: 120,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
    },
]

// ============================================================================
// Week Column Generator (Dynamic columns based on date range)
// ============================================================================

/** Get weeks between start and end date */
const getWeeksInRange = (startDate: Dayjs, endDate: Dayjs): WeekRange[] => {
    const weeks: WeekRange[] = []
    let current = startDate.startOf('isoWeek')
    let weekIndex = 1

    while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
        const weekStart = current.isBefore(startDate) ? startDate : current
        const weekEnd = current.endOf('isoWeek').isAfter(endDate)
            ? endDate
            : current.endOf('isoWeek')

        weeks.push({
            weekNumber: weekIndex,
            start: weekStart,
            end: weekEnd,
            label: `${weekStart.format('DD/MM')} - ${weekEnd.format('DD/MM')}`,
        })

        current = current.add(1, 'week')
        weekIndex++
    }

    return weeks
}

/** Check if note is system-generated */
const isSystemNote = (noteContent: string) => {
    if (!noteContent) return false
    const systemPatterns = [
        /^Chuyển sang bước/i,
        /^Chuyển từ .+ sang/i,
        /^Moved to step/i,
        /^Moved from .+ to/i,
        /^Step changed to/i,
        /^Hoàn thành quá trình/i,
        /^Journey completed/i,
        /^Mở lại từ/i,
        /^Re-engaged from/i,
        /^Đóng deal/i,
        /^Deal closed/i,
        /^Tạo POC/i,
        /^POC created/i,
        /^Tạo hợp đồng/i,
        /^Contract created/i,
        /^step-changed-to/i,
    ]
    return systemPatterns.some((pattern) => pattern.test(noteContent.trim()))
}

/** Filter notes by week range */
const filterNotesByWeek = (
    notes: CustomerJourneyNote[] | undefined,
    weekStart: Dayjs,
    weekEnd: Dayjs
): CustomerJourneyNote[] => {
    if (!notes) return []

    return notes.filter((note) => {
        const noteDate = dayjs(note.customer_journey_note_updated_at)
        const isInWeekRange =
            (noteDate.isAfter(weekStart) ||
                noteDate.isSame(weekStart, 'day')) &&
            (noteDate.isBefore(weekEnd) || noteDate.isSame(weekEnd, 'day'))

        const { text } = parseImportantJourneyNote(
            note?.customer_journey_note || ''
        )
        return isInWeekRange && !isSystemNote(text)
    })
}

/** Step badge colors */
const STEP_COLORS: Record<string, string> = {
    searching: 'bg-blue-100 text-blue-700',
    presentation: 'bg-violet-100 text-violet-700',
    poc: 'bg-amber-100 text-amber-700',
    contract: 'bg-emerald-100 text-emerald-700',
    close_win: 'bg-green-100 text-green-700',
    close_lose: 'bg-red-100 text-red-700',
    terminate: 'bg-gray-100 text-gray-700',
}

/** Week notes cell component — grouped by journey step */
const WeekNotesCell = ({
    notes,
    t,
}: {
    notes: CustomerJourneyNote[]
    t: TFunction
}) => {
    if (!notes || notes.length === 0) {
        return (
            <span className="text-muted-foreground text-xs italic text-gray-400">
                {t('no-activity')}
            </span>
        )
    }

    // Group notes by step
    const grouped = new Map<string, CustomerJourneyNote[]>()
    for (const note of notes) {
        const step = note.customer_journey_note_step || 'unknown'
        if (!grouped.has(step)) grouped.set(step, [])
        grouped.get(step)!.push(note)
    }

    return (
        <div className="flex flex-col gap-1 max-h-[100px] overflow-y-auto py-0.5">
            {[...grouped.entries()].map(([step, stepNotes]) => {
                const stepLabel =
                    CUSTOMER_JOURNEYS(t)?.find((item) => item?.value === step)
                        ?.label || step
                const colorClass =
                    STEP_COLORS[step] || 'bg-gray-100 text-gray-600'
                const parsedNotes = stepNotes.map((n) =>
                    parseImportantJourneyNote(n.customer_journey_note || '')
                )
                const hasImportant = parsedNotes.some((n) => n.isImportant)
                const noteTexts = parsedNotes.map((n) => n.text).join('; ')

                return (
                    <div
                        key={step}
                        className={`flex items-start gap-1 min-w-0 ${hasImportant ? 'bg-red-50 rounded px-1' : ''}`}
                        title={noteTexts}
                    >
                        <span
                            className={`shrink-0 inline-block rounded px-1 py-0.5 text-[10px] font-medium leading-tight ${colorClass}`}
                        >
                            {stepLabel}
                        </span>
                        <span
                            className={`truncate text-xs leading-tight pt-0.5 ${hasImportant ? 'text-red-700 font-medium' : 'text-muted-foreground'}`}
                        >
                            {parsedNotes.length > 1
                                ? `${parsedNotes[0].text} (+${parsedNotes.length - 1})`
                                : parsedNotes[0].text}
                        </span>
                    </div>
                )
            })}
        </div>
    )
}

/** Collect all notes from all journeys in a customer row */
const getAllNotes = (row: CustomerQualifiedProspect): CustomerJourneyNote[] => {
    if (!row?.customer_journey) return []
    return row.customer_journey.flatMap(
        (journey) => journey.customer_journey_notes || []
    )
}

/** Generate dynamic week columns for parent rows (all journeys aggregated) */
export const generateWeekColumns = (
    noteTimeRange: [Dayjs, Dayjs] | undefined,
    t: TFunction
): DataTableColumn<CustomerQualifiedProspect>[] => {
    if (!noteTimeRange || !noteTimeRange[0] || !noteTimeRange[1]) {
        return []
    }

    const weeks = getWeeksInRange(noteTimeRange[0], noteTimeRange[1])

    return weeks.map((week) => ({
        key: `week_${week.weekNumber}`,
        title: `W${week.weekNumber}: ${week.label}`,
        width: 250,
        render: (_: any, row: CustomerQualifiedProspect) => {
            const allNotes = getAllNotes(row)
            const weekNotes = filterNotesByWeek(allNotes, week.start, week.end)
            return <WeekNotesCell notes={weekNotes} t={t} />
        },
    }))
}

/** Generate dynamic week columns for expanded journey rows (single journey) */
export const generateJourneyWeekColumns = (
    noteTimeRange: [Dayjs, Dayjs] | undefined,
    t: TFunction
): DataTableColumn<CustomerJourney>[] => {
    if (!noteTimeRange || !noteTimeRange[0] || !noteTimeRange[1]) {
        return []
    }

    const weeks = getWeeksInRange(noteTimeRange[0], noteTimeRange[1])

    return weeks.map((week) => ({
        key: `week_${week.weekNumber}`,
        title: `W${week.weekNumber}: ${week.label}`,
        width: 250,
        render: (_: any, row: CustomerJourney) => {
            const weekNotes = filterNotesByWeek(
                row.customer_journey_notes,
                week.start,
                week.end
            )
            return <WeekNotesCell notes={weekNotes} t={t} />
        },
    }))
}

// ============================================================================
// Filters
// ============================================================================

export const getJourneyFilterItems = getCommonCustomerFilters
export const JOURNEY_LIST_FILTER_DEFAULTS = {
    ...JOURNEY_FILTER_DEFAULTS,
    only_with_notes: false,
}

// ============================================================================
// Sort Configuration
// ============================================================================

export const JOURNEY_SORT_CONFIG = {
    defaultSort: {
        sort_by: 'customer_updated_at',
        sort_order: 'desc' as const,
    },
    sortableFields: [
        'customer_name',
        'customer_created_at',
        'customer_updated_at',
    ],
}

// ============================================================================
// Utilities
// ============================================================================

/** Get journeys from customer row */
export const getCustomerJourneys = (row: CustomerQualifiedProspect) =>
    row.customer_journey || []

/** Check if customer has journeys */
export const hasJourneys = (row: CustomerQualifiedProspect) =>
    row.customer_journey && row.customer_journey.length > 0

/** Default note time range (4 weeks back) */
export const getDefaultNoteTimeRange = (): [Dayjs, Dayjs] => [
    dayjs().subtract(4, 'week').startOf('day'),
    dayjs().endOf('day'),
]
